// One-time orientation tour shown the FIRST time a visitor exits
// Beginner Mode into the full app. Four lightweight cards, each
// pointing at a primary nav destination.
//
// Trigger logic:
//   - Mount globally in App.tsx.
//   - Watch beginner.active going true → false (transition).
//   - When that transition happens AND exitTourSeen=false AND the user
//     has completed at least 1 skill (so we don't tour someone who
//     opened+closed the shell without doing anything), open the tour.
//   - On finish/skip: mark exitTourSeen so it never fires again.
//
// We keep this as a centered modal-style stack (not anchored coachmarks)
// to avoid coupling to the navbar's exact DOM structure.
import { useEffect, useRef, useState } from "react";
import { Compass, Bot, Library as LibraryIcon, Sparkles, ArrowRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBeginnerMode } from "@/hooks/useBeginnerMode";

const STOPS = [
  {
    icon: Compass,
    title: "The Roadmap",
    where: "/roadmap",
    body: "All 100 skills in one view. Jump to any one, search, or filter by tier. Your guided progress is mirrored here.",
  },
  {
    icon: Bot,
    title: "Agents",
    where: "/agents",
    body: "Build AIs that do tasks for you — search the web, summarize, write reports. Unlocked in guided mode at skill #25.",
  },
  {
    icon: LibraryIcon,
    title: "The Library",
    where: "/library",
    body: "Community-built playbooks for getting anything done. Fork one, run it, save your version.",
  },
  {
    icon: Sparkles,
    title: "Restart guided mode anytime",
    where: "Banner on the home page",
    body: "If you ever want the focused one-skill-at-a-time view back, the 'Start here' banner brings it back.",
  },
];

const FirstExitCoachmarks = () => {
  const { active, completed, exitTourSeen, markExitTourSeen } = useBeginnerMode();
  const prevActive = useRef(active);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Detect the active=true → active=false transition.
  useEffect(() => {
    const wasActive = prevActive.current;
    prevActive.current = active;
    if (wasActive && !active && !exitTourSeen && completed.length > 0) {
      // Slight delay so the shell unmount animation completes first.
      const t = window.setTimeout(() => setOpen(true), 250);
      return () => window.clearTimeout(t);
    }
  }, [active, completed.length, exitTourSeen]);

  const finish = () => {
    markExitTourSeen();
    setOpen(false);
    setStep(0);
  };

  const isLast = step === STOPS.length - 1;
  const Stop = STOPS[step];
  const Icon = Stop.icon;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) finish();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 w-12 h-12 rounded-xl bg-primary/15 grid place-items-center">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center font-mono text-lg">
            {Stop.title}
          </DialogTitle>
          <DialogDescription className="text-center font-mono text-xs pt-2 leading-relaxed">
            {Stop.body}
          </DialogDescription>
          <p className="text-center text-[10px] font-mono text-muted-foreground mt-2">
            <span className="text-primary">{Stop.where}</span>
          </p>
        </DialogHeader>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 my-3">
          {STOPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-primary" : "w-1.5 bg-secondary"
              }`}
            />
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {isLast ? "Got it" : "Next"}
            <ArrowRight className="w-4 h-4" />
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={finish}
              className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground"
            >
              <X className="w-3 h-3" />
              Skip tour
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FirstExitCoachmarks;
